/**
 * Dev-only **Feature toggles** — force a web feature flag On or Off in THIS browser, the web port of the
 * native apps' Dev Tools ▸ Feature Toggles screen (`ios/.../FeatureTogglesScreen.swift`,
 * `android/.../FeatureToggles.kt`).
 *
 * Same model as native, deliberately:
 *
 *   - Every toggle the app conducts is listed (`WEB_TOGGLES`), with its **natural** conducted state
 *     shown on the Default pill ("Default · On" / "Default · Off").
 *   - Per toggle a **tri-state**: Default (no override) · On (`"true"`) · Off (`"false"`).
 *   - A **Persist** switch (default ON) deciding whether the override survives a reload — native's
 *     "survives a relaunch". Persisted overrides go to `localStorage`; session-only ones to
 *     `sessionStorage`, which dies with the tab exactly as native's in-memory override dies with the
 *     process.
 *
 * The flag read-path consults `toggleOverride()` FIRST (see `lib/instance-auth-flag.ts`), so a forced
 * value behaves like the real conducted one everywhere downstream — no special-casing per call site.
 *
 * Every entry point is gated on `devToolsUnlocked()`, the same gate as the mock layer: a real user's
 * browser holds no grant, so a key left in storage (or hand-written) does nothing at all.
 */

import { devToolsUnlocked } from './dev-tools';

/** A forced value. Kept as the string native uses, so the two stores read identically. */
export type ToggleOverride = 'true' | 'false';

export interface WebToggle {
  /** The Petri/Conductor spec id — what the server conducts. */
  spec: string;
  /** What it gates, in one line, for the panel. */
  description: string;
}

/** The signed-instance auth gate. Exported so the flag's own module reads the same literal. */
export const INSTANCE_AUTH_SPEC = 'symphonyInstanceAuthWeb';

/** The live voice-call gate. Exported so the flag's own module reads the same literal. */
export const VOICE_CALL_SPEC = 'symphonyVoiceCallWeb';

/** The Credit Miner in the image placeholder. Exported so the flag's own module reads the same literal. */
export const CREDIT_MINER_SPEC = 'symphonyCreditMinerWeb';

/**
 * The out-of-credits "earn credits instead" offer. Exported so the flag's own module reads the same
 * literal — and the NATIVE apps conduct this exact spec id, so the three surfaces ramp together.
 */
export const EARN_CREDITS_OFFER_SPEC = 'symphonyOutOfCreditsEarnOfferEnabled';

/**
 * The package picker's billing-cycle-step arm.
 *
 * **Deliberately NOT `…Web` like its three siblings above.** This is the key Petri actually holds
 * (`fetchExperimentById 5209969`), and `conduct` matches the string exactly — so the panel has to
 * name the same one the server asks for. It read `symphonyBillingCycleStepWeb` while Petri held
 * `specs.orion.SymphonyBillingCycleStep`, which is what left an allowlist of three named users on
 * the old picker with the experiment looking correctly configured.
 *
 * The `orion` in it is a live identifier, not prose, so the rename does not touch it (CLAUDE.md).
 *
 * It genuinely is the only surface that conducts this — but NOT because the phones have a picker of
 * their own. iOS and Android both send buyers to this exact page (`CreditsUpgrade.symphonyURL` ->
 * symphony.wix.com/packages), so all three surfaces are already served by this one gate.
 */
export const BILLING_CYCLE_STEP_SPEC = 'specs.orion.SymphonyBillingCycleStep';

/**
 * The daily-limit bar's rollout gate — the second meter reading how much of today's allowance is
 * spent (Figma 15375-104311).
 *
 * **This is the CAP's own key, not a web arm of its own**, so the bar rolls out to exactly the
 * population the ceiling applies to rather than ramping out of step with it. Hence bare camelCase
 * with no `…Web` suffix and no `specs.` prefix — do not "correct" it to either shape.
 *
 * ⚠️ Which object answers this key has been read wrong once — see the route for the evidence and for
 * why "there are two objects" is NOT the settled conclusion it was first written as. Survivable either
 * way, because the bar also requires the BFF to have sent the numbers.
 *
 * Forcing this On in the panel does NOT conjure a bar on an account the BFF reports no ceiling for —
 * the data is the other half of the gate, and it is the half that cannot be overridden.
 */
export const DAILY_LIMIT_BAR_SPEC = 'symphonyDailyCreditsLimit';

/**
 * The AGENTS WORLD home — the free-camera map. One of the three homes, each with its own release.
 *
 * A single `symphonyHome2Web` release used to gate "the new homes" as a group while a localStorage
 * dropdown chose between them, so a cohort could be given the new homes but never a PARTICULAR one,
 * and two people in the same cohort saw different screens. Now the release names the home, there is
 * no dropdown, and that retired release is gone along with its flag module and `/api/flags/home2`.
 *
 * **Web-only on purpose** — the phones ramp `specs.symphony.SymphonyAgentsWorldHomeMobile` for the same home, so the
 * surfaces move independently.
 */
export const AGENTS_WORLD_HOME_SPEC = 'specs.symphony.SymphonyAgentsWorldHomeWeb';

/**
 * The ACTIVITY MAP home — the Overview screen, whose own header switches between the task list and the
 * map hosted inside it. Wins over [AGENTS_WORLD_HOME_SPEC] for an account in both cohorts
 * (`resolveHomeVariant`): the newer rollout has to be able to move somebody already in the older one.
 *
 * **Web-only on purpose** — the phones ramp `specs.symphony.SymphonyActivityMapHomeMobile`.
 */
export const ACTIVITY_MAP_HOME_SPEC = 'specs.symphony.SymphonyActivityMapHomeWeb';

/**
 * Hides the ACTIVITY MAP home's header switch (task list ⇄ map) on the desktop, so that home is the
 * task list alone. Only means something inside [ACTIVITY_MAP_HOME_SPEC]; mobile web keeps the switch.
 */
export const ACTIVITY_MAP_HIDE_TABS_SPEC = 'specs.symphony.SymphonyActivityMapHideTabsWeb';

/**
 * The Notifications page's "What's new" tab. On = a Notifications / What's new tab row, the product-update
 * list behind the second tab, and `?tab=whats-new` opens it. Off (and while the gate is loading) = the
 * Notifications page exactly as it was: no tab row, no `/api/whats-new` read, and `?tab=whats-new` ignored.
 *
 * A Conductor feature release, ONE id for web, iOS and Android, so the tab opens on every surface at once.
 */
export const WHATS_NEW_SPEC = 'symphonyWhatsNew';

/**
 * The chat FILE PANE: a file opened from the chat docks beside the conversation instead of the modal above
 * it, and refreshes live as the agent rewrites it (`lib/chat-file-pane.ts`, `lib/use-live-doc-file.ts`).
 * Control is the app as it shipped before: the modal, loaded once. Web only for now.
 *
 * A CONDUCTOR experiment id (conductor-server `conduct`, treatment = Variant B), not a Petri spec — the
 * call must match the system the experiment is authored in (see `pages/api/flags/agents-world-home.ts`).
 */
export const CHAT_FILE_PANE_SPEC = 'symphonyChatFilePaneWeb';

/**
 * The "personal sale" limited-time welcome offer — the top banner (a 24h countdown from the recipient's
 * createdDate) plus the "30% OFF" badge on the drawer credits card. Variant (`true`) shows them to a NEW
 * FREE-plan account while inside its 24h window; control is the app without them. Off until conducted, so
 * nobody outside the ramp sees them, and it shows nothing for a paid account or one past its window — the
 * plan and the date are the other halves of the gate and cannot be forced from here (`use-personal-sale.ts`).
 *
 * This is the SAME spec the phones conduct: web reads it on every surface, so a picker open reads the FT
 * the account was ramped on regardless of the surface it came from.
 */
export const PERSONAL_SALE_MOBILE_SPEC = 'symphonyPersonalSaleMobile';

/**
 * The Meetings channel — her joining the account's own video calls. Variant (`true`) shows the
 * Meetings section on the Channels tab and its node on the hero diagram; control shows neither.
 *
 * **Forcing this On does NOT get you a working card.** The three `/api/meetings/*` proxies conduct the
 * same spec server-side and answer 501 to an account outside the release, so an override reveals the
 * section and nothing behind it. That split is deliberate: this feature's Connect button writes a
 * connection row that lets a bot into somebody's meetings, which is not a thing a browser-side
 * override may grant.
 *
 * Bare camelCase with no `…Web` suffix: the phones have no Meetings section yet (`meetings` is absent
 * from both native `ChannelSlug` enums), and when they get one they conduct this same spec so the
 * three surfaces ramp together.
 */
export const MEETINGS_AGENT_SPEC = 'symphonyMeetingsAgent';

/**
 * Alfred on the Connectors › Outreach tab — `alfredInSymphonyOutreach`. On = the coming-soon Phone row is
 * gone and Alfred (the AI phone receptionist) sits above iMessage, with Connect / a Connected badge and
 * Manage. Off = today's list, Phone row and vote included.
 *
 * **Forcing this On draws nothing by itself.** The row is built from the server's Alfred connector entry,
 * which exists only for an account inside `alfredSymphonyConnector` (the server's own gate) and whose
 * status read succeeded. Without one there is no Alfred row AND no Phone row — the tab never offers a
 * Connect over a receptionist it cannot see. Bare camelCase, no `…Web` suffix: the phones conduct this
 * same spec, so the three surfaces ramp together.
 */
export const ALFRED_IN_OUTREACH_SPEC = 'alfredInSymphonyOutreach';

/**
 * Alfred's onboarding — `alfredOnboardingInSymphony`. On = the Outreach Alfred row's Connect opens the two-step
 * setup (`AlfredSetupModal`: "Get my number", then how to forward to it). Off = Connect is the chat handoff
 * it was before. Rides on the row, so it does nothing while `alfredInSymphonyOutreach` hides it.
 *
 * **Forcing this On cannot buy a number by itself.** The setup endpoints conduct the same toggle on the
 * caller, and `alfredSymphonyConnector` with it, and refuse when either is closed. Bare camelCase, no `…Web`
 * suffix: the phones conduct this same spec.
 */
export const ALFRED_ONBOARDING_SPEC = 'alfredOnboardingInSymphony';

/**
 * Alfred's settings screen — `alfredSettingsInSymphony`. On = Manage on the connected Outreach Alfred row opens
 * `/alfred` (number, call forwarding, and how Alfred sounds, each change handed to chat). Off = Manage opens
 * chat, as before. Rides on the row, so it does nothing while `alfredInSymphonyOutreach` hides it.
 *
 * **Forcing this On shows nothing the server would not.** The settings read conducts the same toggle on the
 * caller, and `alfredSymphonyConnector` with it, and the page sends a refused read back to the row. Bare
 * camelCase, no `…Web` suffix: the phones conduct this same spec.
 */
export const ALFRED_SETTINGS_SPEC = 'alfredSettingsInSymphony';

/**
 * The Outreach screen's Calls tab — `symphonyOutreachCallTab`. On = a third tab after Inbox: Alfred's Connect
 * while it is not set up, its number once it is. Off = the two tabs every user has. Opened to the same
 * audience as `alfredOnboardingInSymphony`, whose server gate the tab's number read sits behind. Bare
 * camelCase, no `…Web` suffix: the phones conduct this same spec.
 */
export const OUTREACH_CALL_TAB_SPEC = 'symphonyOutreachCallTab';

/**
 * Every toggle this app conducts. Add one ONLY when something actually reads it — same rule as
 * native's `ExperimentSpecs.startup`: a spec nobody reads is noise in that experiment's analysis.
 */
export const WEB_TOGGLES: WebToggle[] = [
  {
    spec: INSTANCE_AUTH_SPEC,
    description: 'Signed-instance auth for the data layer (off = the legacy credentialed fetch).',
  },
  {
    spec: VOICE_CALL_SPEC,
    description: 'Live voice calls. Voice is ON by default; set this release to control to turn it off.',
  },
  {
    spec: CREDIT_MINER_SPEC,
    description: 'The Credit Miner game inside the image-render placeholder. Off unless the server gate or this override opens it.',
  },
  {
    spec: EARN_CREDITS_OFFER_SPEC,
    description:
      'The "Earn free credits" offer at the out-of-credits moments (rail credits card, paused routine, buy-credits). Off until conducted.',
  },
  {
    spec: BILLING_CYCLE_STEP_SPEC,
    description:
      'Package picker A/B: On = no Monthly/Yearly toggle, the cycle is chosen on a second screen after the plan. Off = today’s one-screen picker.',
  },
  {
    spec: DAILY_LIMIT_BAR_SPEC,
    description:
      'The daily-limit bar on the credits surfaces (today’s allowance, beside the period meter). Shows nothing unless the account is actually under a per-day ceiling — the numbers come from the server and cannot be forced.',
  },
  {
    spec: AGENTS_WORLD_HOME_SPEC,
    description:
      'The Agents World home: On = the free-camera map is your home, and the rail drops its own “Your agents” entry because this home IS that screen. Off (and no other home on) = today’s catch-up home.',
  },
  {
    spec: ACTIVITY_MAP_HOME_SPEC,
    description:
      'The Activity Map home: On = the Overview screen is your home — the task list, with a switch in its header to the activity map hosted inside it. Wins over Agents World if both are on. Off (and no other home on) = today’s catch-up home.',
  },
  {
    spec: CHAT_FILE_PANE_SPEC,
    description:
      'Chat file pane: On = a file opened from the chat opens beside it (wide layouts) and updates live while the agent edits it. Off = the file opens in the modal above the chat, as before.',
  },
  {
    spec: ACTIVITY_MAP_HIDE_TABS_SPEC,
    description:
      'Activity Map home tabs: On = no task-list / map switch in the Overview header on the desktop, so the home is the task list only. Off = the switch is there. Mobile web keeps the switch either way. Only visible with the Activity Map home on.',
  },
  {
    spec: WHATS_NEW_SPEC,
    description:
      'What\'s new: On = the Notifications page has a Notifications / What\'s new tab row, and the second tab lists product updates. Off = the Notifications page as it was, with no tabs and no What\'s new read.',
  },
  {
    spec: MEETINGS_AGENT_SPEC,
    description:
      'The Meetings channel: On = the Meetings section on Channels (connect her to your video calls, and the week’s meetings). Off = no section and no hero node. The /api/meetings routes conduct this server-side too, so forcing it On shows the card and still gets 501s unless the account is really in the release.',
  },
  {
    spec: PERSONAL_SALE_MOBILE_SPEC,
    description:
      'Personal sale: On = the limited-time welcome banner (24h countdown) + the "30% OFF" badge on the credits card. Off = neither. Shows nothing unless the account is Free AND still inside its 24h window — the plan and the recipient date are the other half of the gate. Use the personal-sale preview to see it regardless. (The phones conduct this same spec.)',
  },
  {
    spec: ALFRED_IN_OUTREACH_SPEC,
    description: 'Outreach tab: the Phone row becomes an Alfred row (needs the server\'s alfredSymphonyConnector to list Alfred).',
  },
  {
    spec: ALFRED_ONBOARDING_SPEC,
    description:
      'Alfred onboarding: On = the Outreach Alfred row\'s Connect opens the two-step setup (number, then call forwarding). Off = Connect opens chat. The setup endpoints check the same toggle on the server.',
  },
  {
    spec: ALFRED_SETTINGS_SPEC,
    description:
      'Alfred settings: On = the Outreach Alfred row\'s Manage opens the /alfred settings page (number, call forwarding, how Alfred sounds). Off = Manage opens chat. The settings read checks the same toggle on the server.',
  },
  {
    spec: OUTREACH_CALL_TAB_SPEC,
    description:
      'Outreach Calls tab: On = a third tab on the Outreach screen with Alfred\'s Connect, or its number once connected. Off = Contacts and Inbox only. The number needs alfredOnboardingInSymphony on the server.',
  },
];

const PREFIX = 'orion-web:dev-ft:';
const key = (spec: string): string => `${PREFIX}${spec}`;

function store(persist: boolean): Storage | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    return persist ? window.localStorage : window.sessionStorage;
  } catch {
    return undefined; // storage blocked — no overrides, the conducted value stands
  }
}

function read(persist: boolean, spec: string): ToggleOverride | undefined {
  try {
    const value = store(persist)?.getItem(key(spec));
    return value === 'true' || value === 'false' ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The forced value for a spec, or `undefined` for "use the conducted value". Persisted overrides win
 * over session ones (a Persist=on write clears the session copy, so both are never set at once).
 *
 * Safe to call from anywhere, including the flag read-path: without a dev-tools grant it is always
 * `undefined`, so this cannot change what a real user's app does.
 */
export function toggleOverride(spec: string): ToggleOverride | undefined {
  if (!devToolsUnlocked()) return undefined;
  return read(true, spec) ?? read(false, spec);
}

/** Whether this spec's override is flagged to survive a reload (native: `isOverridePersisted`). */
export function isOverridePersisted(spec: string): boolean {
  if (!devToolsUnlocked()) return false;
  return read(true, spec) !== undefined;
}

/**
 * Set or clear an override (native: `setDevOverride`).
 *
 * - `value` `null` → back to Default: dropped from BOTH stores.
 * - otherwise → written to the store `persist` selects, and removed from the other, so flipping Persist
 *   moves the override rather than leaving a stale twin behind.
 */
export function setToggleOverride(spec: string, value: ToggleOverride | null, persist: boolean): void {
  if (!devToolsUnlocked()) return;
  try {
    if (value === null) {
      store(true)?.removeItem(key(spec));
      store(false)?.removeItem(key(spec));
      return;
    }
    store(persist)?.setItem(key(spec), value);
    store(!persist)?.removeItem(key(spec));
  } catch {
    /* quota / blocked storage — the panel simply shows no override */
  }
}

/** Drop every override (the panel's "Use conducted values"). */
export function clearToggleOverrides(): void {
  if (!devToolsUnlocked()) return;
  for (const { spec } of WEB_TOGGLES) setToggleOverride(spec, null, true);
}

/** How many toggles are currently forced — what the dev menu badges itself with. */
export function overriddenToggleCount(): number {
  return WEB_TOGGLES.filter(({ spec }) => toggleOverride(spec) !== undefined).length;
}
